/**
 * Document entity DTOs.
 *
 * Mirrors Prisma `Document` + `DocumentVersion` (prisma/schema.prisma:569,
 * :591). Every Document belongs to a Project; a Document can have many
 * versions, and `currentVersionId` points to the version the UI should
 * surface by default.
 *
 * DocumentType enum is the closed Prisma set:
 *   RAMS | CPP | TOOLBOX_TALK | SITE_INDUCTION | EMERGENCY_PLAN | COSHH | SIGN_OFF
 *
 * `SIGN_OFF` is system-generated. COSHH can be started from the native
 * document picker using the project COSHH register.
 *
 * DocumentVariant is a contracts-package construct that does NOT exist in
 * Prisma — Lightweight CPP persists as DocumentType.CPP with a wizard-time
 * flag (see CLAUDE.md memory note `project_lightweight_cpp`). The mobile
 * client uses `variant` to render the correct wizard.
 */
import { z } from "zod";
import { entityIdSchema } from "../common";
import { DOCUMENT_TYPES } from "../document-meta";

// The DocumentType union + per-type metadata registry live in the zod-free
// `../document-meta` module (exposed as the `@thesitebook/contracts/document-meta`
// subpath so client bundles can import label helpers without the full schema
// graph); re-exported here so the package-root API is unchanged for
// server/mobile consumers.
export {
  DOCUMENT_TYPES,
  DOCUMENT_TYPE_META,
  GENERATABLE_DOCUMENT_TYPES,
  documentTypeLabel,
  documentTypeFilenameLabel,
} from "../document-meta";
export type { DocumentType, DocumentTypeMeta } from "../document-meta";

export const DocumentTypeSchema = z.enum(DOCUMENT_TYPES);

/**
 * Mobile-only marker for the CPP variant. FULL = standard CPP wizard,
 * LIGHTWEIGHT = the sole-trader / domestic-client wizard (see
 * `isLightweightCppEligible` in lib/cpp-eligibility.ts).
 *
 * NULL when the document is not a CPP — the field is always present so
 * mobile clients can read `doc.variant ?? "FULL"` safely.
 */
export const DOCUMENT_VARIANTS = ["FULL", "LIGHTWEIGHT"] as const;

export type DocumentVariant = (typeof DOCUMENT_VARIANTS)[number];

export const DocumentVariantSchema = z.enum(DOCUMENT_VARIANTS);

export const DOCUMENT_STATUSES = [
  "DRAFT",
  "DRAFT_PREVIEW",
  "GENERATED",
  "SHARED",
  "SIGNED",
  "ISSUABLE",
  "ARCHIVED",
] as const;

export type DocumentStatus = (typeof DOCUMENT_STATUSES)[number];

export const DocumentStatusSchema = z.enum(DOCUMENT_STATUSES);

/**
 * Compact representation used in list views and inside ProjectResponse.
 * Phase 11 mobile screens hydrate the full DocumentVersion separately.
 */
export const DocumentSummarySchema = z.object({
  id: entityIdSchema,
  projectId: entityIdSchema,
  documentType: DocumentTypeSchema,
  variant: DocumentVariantSchema.nullable(),
  status: DocumentStatusSchema,
  currentVersionId: entityIdSchema.nullable(),
  currentVersionNumber: z.number().int().nullable(),
  needsReview: z.boolean(),
  lastReviewedAt: z.string().datetime().nullable(),
  reviewDueAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type DocumentSummary = z.infer<typeof DocumentSummarySchema>;

/**
 * One version summary as it rides inside `DocumentSchema.versions`. Hoisted
 * to its own definition so the #2411 server-boundary strict variant below
 * can re-strict this level without duplicating the field list.
 */
const DocumentVersionSummarySchema = z.object({
  id: entityIdSchema,
  versionNumber: z.number().int(),
  generatedPdfUrl: z.string(),
  signedPdfUrl: z.string().nullable(),
  // D3 — true once `signedPdfUrl` reflects the LATEST signature. A
  // signed copy can still be rendering (or a process can have died
  // mid-render) right after a signature was captured; the screen
  // should hide/disable its "download signed copy" affordance while
  // this is false rather than offering a stale or missing file.
  signedCopyReady: z.boolean(),
  specificityScore: z.number().int().nullable(),
  createdAt: z.string().datetime(),
});

export const DocumentSchema = DocumentSummarySchema.extend({
  versions: z.array(DocumentVersionSummarySchema),
});

export type Document = z.infer<typeof DocumentSchema>;

// ─── #2411 server-boundary variants ─────────────────────────────────────────
// The mobile app parses the permissive bases above; the routes serialise
// through these Strict forms (`DocumentSchema` is strict at BOTH its own level
// and the versions element, so the rebuild restates both).

export const DocumentSummaryStrict = DocumentSummarySchema.strict();

export const DocumentStrict = DocumentSchema.extend({
  versions: z.array(DocumentVersionSummarySchema.strict()),
}).strict();

/**
 * DocumentVersion DTO. `payload` is intentionally `z.unknown()` because the
 * shape varies per documentType (RAMS payload != CPP payload). Phase 12+
 * adds per-type payload schemas if/when mobile needs to read them directly.
 */
export const DocumentVersionSchema = z
  .object({
    id: entityIdSchema,
    documentId: entityIdSchema,
    templateId: entityIdSchema,
    templateVersionNumber: z.number().int(),
    versionNumber: z.number().int(),
    payload: z.unknown(),
    generatedPdfUrl: z.string(),
    signedPdfUrl: z.string().nullable(),
    specificityScore: z.number().int().nullable(),
    // Generator provenance, not ownership — null once the creator deleted
    // their account (DocumentVersion.createdByUserId is SetNull so issued
    // documents outlive their authors).
    createdByUserId: z.string().nullable(),
    createdAt: z.string().datetime(),
  })
  .strict();

export type DocumentVersion = z.infer<typeof DocumentVersionSchema>;

/**
 * POST /api/mobile/v1/projects/:projectId/documents body. Creates a new
 * document of `documentType` (and `variant` when documentType is CPP).
 * Generation answers land via a follow-up POST to the type-specific
 * generation endpoint — see lib/validators.ts::generateDocumentSchema for
 * the (long) shape, which mobile reuses unchanged.
 */
export const DocumentCreateRequest = z
  .object({
    documentType: DocumentTypeSchema,
    variant: DocumentVariantSchema.optional().describe(
      "Required when documentType is CPP. Ignored for other types.",
    ),
  })
  .strict();

export type DocumentCreateRequest = z.infer<typeof DocumentCreateRequest>;

export const DocumentResponse = z
  .object({ document: DocumentSchema })
  .strict();
export type DocumentResponse = z.infer<typeof DocumentResponse>;
